import * as React from 'react'
import { X } from 'lucide-react'
import { Drawer as DrawerPrimitive } from 'vaul'

import { cn } from '@modules/lib/lib'

const Drawer = ({
  shouldScaleBackground = true,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root shouldScaleBackground={shouldScaleBackground} {...props} />
)
Drawer.displayName = 'Drawer'

const DrawerTrigger = DrawerPrimitive.Trigger

const DrawerPortal = DrawerPrimitive.Portal

const DrawerClose = DrawerPrimitive.Close

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    className={cn('fixed inset-0 z-50 bg-gray-900/50', className)}
    ref={ref}
    {...props}
  />
))
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
    direction?: 'right' | 'top' | 'bottom' | 'left' | undefined
  }
>(({ className, children, direction = 'top', ...props }, ref) => {
  let contentStyle =
    'fixed inset-x-0 bottom-0 z-50 mt-24 flex h-auto flex-col rounded-t-2xl border bg-background'
  if (direction === 'bottom') {
    contentStyle =
      'fixed inset-x-0 bottom-0 z-50 mt-24 flex h-auto flex-col rounded-t-2xl border bg-background'
  }
  if (direction === 'top') {
    contentStyle =
      'fixed inset-x-0 top-0 z-50 mt-24 flex h-auto flex-col rounded-b-[10px] border bg-background'
  }
  if (direction === 'right') {
    contentStyle = 'bg-white flex flex-col h-full w-[300px] mt-24 fixed bottom-0 right-0 z-50'
  }
  if (direction === 'left') {
    contentStyle = 'bg-white flex flex-col h-full w-[300px] mt-24 fixed bottom-0 left-0 z-50'
  }
  return (
    <DrawerPortal>
      <DrawerOverlay />
      <DrawerPrimitive.Content className={cn(contentStyle, className)} ref={ref} {...props}>
        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  )
})
DrawerContent.displayName = 'DrawerContent'

const DrawerHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('grid gap-1.5 p-4 text-center sm:text-left', className)} {...props} />
)
DrawerHeader.displayName = 'DrawerHeader'

const DrawerFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('mt-auto flex flex-col gap-2 p-4', className)} {...props} />
)
DrawerFooter.displayName = 'DrawerFooter'

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    ref={ref}
    {...props}
  />
))
DrawerTitle.displayName = DrawerPrimitive.Title.displayName

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    className={cn('text-sm text-muted-foreground', className)}
    ref={ref}
    {...props}
  />
))
DrawerDescription.displayName = DrawerPrimitive.Description.displayName

type Props = React.PropsWithChildren<{
  direction?: 'right' | 'top' | 'bottom' | 'left' | undefined
  title?: string
  description?: string
  contentWrapperClassName?: string
  contentInnerClassName?: string
  triggerAsChild?: boolean
  toggle: React.ReactElement
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}>

const DrawerComponent: React.FC<Props> = ({
  direction,
  title,
  description,
  contentWrapperClassName,
  contentInnerClassName,
  triggerAsChild = false,
  toggle,
  open,
  setOpen,
  children,
}) => {
  return (
    <Drawer direction={direction} onOpenChange={setOpen} open={open}>
      <DrawerTrigger asChild={triggerAsChild}>{toggle}</DrawerTrigger>
      <DrawerContent direction={direction}>
        <div className="flex justify-end p-4 border-b">
          <DrawerClose>
            <X id="del" size={24} />
          </DrawerClose>
        </div>
        <div className={cn('mx-auto w-full max-w-sm', contentWrapperClassName)}>
          {(title || description) && (
            <DrawerHeader>
              {title && <DrawerTitle>{title}</DrawerTitle>}
              {description && <DrawerDescription>{description}</DrawerDescription>}
            </DrawerHeader>
          )}

          <div className={cn('p-4 pb-0', contentInnerClassName)}>{children}</div>
          <DrawerFooter>{/* 入れたいものが今は無いので一旦空白 */}</DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  )
}

export {
  Drawer,
  DrawerClose,
  DrawerComponent,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
}
